import { useEffect, useState } from 'react';
import { ReactNode } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import Button from '@alltrails/shared/denali/components/Button';
import { executeRecaptcha, loadRecaptcha } from 'utils/recaptcha_helpers';
import * as formStyles from 'components/forms/styles/styles.module.scss';
import OutlinedTextField from 'components/forms/textFields/OutlinedTextField';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import User from '@alltrails/shared/types/User';
import isEmail from 'validator/lib/isEmail';
import useEnableRecaptcha from 'hooks/useEnableRecaptcha';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import { emailLoginHandler } from 'utils/AuthHandlers';

const messages = defineMessages({
  logIn: {
    defaultMessage: 'Log in'
  }
});

type Props = {
  handleLoginSuccess: ({ user }: { user: User }) => void;
  returnTo?: string;
};

function validateEmail(value: string) {
  if (!isEmail(value)) {
    return <FormattedMessage defaultMessage="Email is not valid." />;
  }

  return undefined; // text field uses undefined
}

function validatePassword(value: string) {
  if (value.length < 6) {
    return <FormattedMessage defaultMessage="Password must be 6 characters long." />;
  }

  return undefined; // text field uses undefined
}

export default function EmailPasswordLogin({ handleLoginSuccess, returnTo = '/' }: Props) {
  const {
    formattedDefaultMessages: { logIn }
  } = useFormatMessage(messages);

  const enableRecaptcha = useEnableRecaptcha();
  const languageRegionCode = useLanguageRegionCode();
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userEmailError, setUserEmailError] = useState<ReactNode>();
  const [userPasswordError, setUserPasswordError] = useState<ReactNode>();
  const [serverError, setServerError] = useState<ReactNode>();
  const [disableSubmit, setDisableSubmit] = useState(enableRecaptcha);
  const [submitting, setSubmitting] = useState(false);
  const [showErrorsOnType, setShowErrorsOnType] = useState(false);
  const [recaptchaObject, setRecaptchaObject] = useState();

  useEffect(() => {
    if (enableRecaptcha) {
      loadRecaptcha((recaptchaObject: any) => {
        setDisableSubmit(false);
        setRecaptchaObject(recaptchaObject);
      });
    }
  }, [enableRecaptcha]);

  function failedFormValidation() {
    const emailError = validateEmail(userEmail);
    const passwordError = validatePassword(userPassword);

    setUserEmailError(emailError);
    setUserPasswordError(passwordError);

    return emailError || passwordError;
  }

  function loginSuccessCallback(data: any) {
    if (handleLoginSuccess) {
      handleLoginSuccess(data);
    } else {
      window.location.href = data.welcome_notice ? `${returnTo + (returnTo.includes('?') ? '&' : '?')}welcome=${data.welcome_notice}` : returnTo;
    }
  }

  function loginErrorCallback(err: any) {
    if (err && err.responseJSON && err.responseJSON.errors) {
      setServerError(err.responseJSON.errors[0].message);
    } else {
      setServerError(<FormattedMessage defaultMessage="Error, please try again" />);
    }

    setSubmitting(false);
  }

  function submitLogin(recaptchaToken?: string) {
    emailLoginHandler(
      {
        create_session: true,
        email: userEmail,
        password: userPassword,
        recaptcha: recaptchaToken
      },
      languageRegionCode,
      loginSuccessCallback,
      loginErrorCallback,
      () => null
    );
  }

  function handleChange(e: any) {
    e.preventDefault();
    const { name, value } = e.target;

    // we don't want to show errors on type unless a user has already tried to submit a form
    if (showErrorsOnType) {
      if (name === 'userEmail') {
        setUserEmailError(validateEmail(value));
      }

      if (name === 'userPassword') {
        setUserPasswordError(validatePassword(value));
      }
    }
  }

  function handleLogin(e: React.FormEvent) {
    e.preventDefault();

    if (disableSubmit || submitting) {
      return;
    }

    if (failedFormValidation()) {
      setShowErrorsOnType(true);
      return;
    }

    setSubmitting(true);
    // Need to get recaptchaToken here/now, because a token can only be used once,
    // and server may reject form details requiring the user to edit and resubmit.
    if (recaptchaObject) {
      executeRecaptcha(recaptchaObject, 'userSignupLogin').then(
        (recaptchaToken: string) => submitLogin(recaptchaToken),
        () => setSubmitting(false)
      );
    } else {
      submitLogin();
    }
  }

  return (
    <form id="login-form" onSubmit={handleLogin}>
      <div className={formStyles.inputItem}>
        <OutlinedTextField
          autoFocus
          changeHandler={e => {
            handleChange(e);
            setUserEmail(e.target.value);
          }}
          enterKeyHandler={handleLogin}
          errorMessage={userEmailError}
          label={<FormattedMessage defaultMessage="Email address" />}
          name="userEmail"
          type="email"
          value={userEmail}
        />
      </div>
      <div className={formStyles.inputItem}>
        <OutlinedTextField
          changeHandler={e => {
            handleChange(e);
            setUserPassword(e.target.value);
          }}
          enterKeyHandler={handleLogin}
          errorMessage={userPasswordError}
          label={<FormattedMessage defaultMessage="Password" />}
          name="userPassword"
          type="password"
          value={userPassword}
        />
      </div>
      {serverError && <div className={formStyles.serverError}>{serverError}</div>}
      <Button
        text={logIn}
        disabled={disableSubmit || submitting}
        onClick={() => {
          /* do nothing - form handles submit */
        }}
        testId="email-password-login-button"
        type="submit"
        variant="primary"
        width="full"
      />
    </form>
  );
}
